import { jsPDF } from 'jspdf'
import { BRSR_CORE_CATALOGUE_STATUS, BRSR_CORE_METRICS } from '../../data/brsr/brsrCore'
import { NGRBC_PRINCIPLES, REGULATORY_SOURCES, sourceById } from '../../data/brsr/regulatorySources'
import type { BrsrResult } from '../../data/brsr/types'
import { BRSR_TOOL_URL, validatePdfInput } from './validation'

const INK: [number, number, number] = [26, 32, 44]
const MUTE: [number, number, number] = [90, 104, 120]
const ACCENT: [number, number, number] = [11, 110, 79]
const LINE: [number, number, number] = [226, 232, 240]
const MARGIN = 16
const PAGE_W = 210
const CONTENT_W = PAGE_W - MARGIN * 2
const BOTTOM = 280

type Doc = jsPDF

function setFill(doc: Doc, rgb: [number, number, number]): void {
  doc.setFillColor(rgb[0], rgb[1], rgb[2])
}

function setText(doc: Doc, rgb: [number, number, number]): void {
  doc.setTextColor(rgb[0], rgb[1], rgb[2])
}

function setDraw(doc: Doc, rgb: [number, number, number]): void {
  doc.setDrawColor(rgb[0], rgb[1], rgb[2])
}

function footer(doc: Doc): void {
  const pages = doc.getNumberOfPages()
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page)
    setText(doc, MUTE)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    doc.text('Self-assessment. Not legal advice, a SEBI determination, or an assurance opinion.', MARGIN, 290)
    doc.text(`${page} / ${pages}`, PAGE_W - MARGIN, 290, { align: 'right' })
  }
}

function ensure(doc: Doc, y: number, needed: number): number {
  if (y + needed <= BOTTOM) return y
  doc.addPage()
  setText(doc, ACCENT)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  doc.text('EcopsHub  ·  India BRSR Readiness Checker', MARGIN, 12)
  setDraw(doc, LINE)
  doc.line(MARGIN, 15, PAGE_W - MARGIN, 15)
  return 22
}

function heading(doc: Doc, y: number, text: string): number {
  y = ensure(doc, y, 14)
  setText(doc, ACCENT)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(13)
  doc.text(text, MARGIN, y)
  return y + 8
}

function paragraph(doc: Doc, y: number, text: string, size = 9.5): number {
  const lines = doc.splitTextToSize(text, CONTENT_W) as string[]
  y = ensure(doc, y, lines.length * 4.4 + 2)
  setText(doc, INK)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(size)
  doc.text(lines, MARGIN, y)
  return y + lines.length * 4.4 + 2
}

function bullet(doc: Doc, y: number, text: string): number {
  const lines = doc.splitTextToSize(text, CONTENT_W - 6) as string[]
  y = ensure(doc, y, lines.length * 4.2 + 1)
  setText(doc, INK)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.text('•', MARGIN, y)
  doc.text(lines, MARGIN + 5, y)
  return y + lines.length * 4.2 + 1
}

export function renderBrsrPdf(result: BrsrResult): jsPDF {
  const errors = validatePdfInput(result)
  if (errors.length) {
    throw new Error(errors.join(' '))
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  setFill(doc, ACCENT)
  doc.rect(0, 0, PAGE_W, 297, 'F')
  setText(doc, [255, 255, 255])
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(12)
  doc.text('ECOPSHUB', MARGIN, 28)
  doc.setFontSize(26)
  const title = doc.splitTextToSize('India BRSR Readiness Checker', CONTENT_W) as string[]
  doc.text(title, MARGIN, 52)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(12)
  doc.text('Self-assessment report', MARGIN, 78)
  doc.setFontSize(11)
  const coverLines = [
    `Company: ${result.companyName}`,
    `Reporting year: ${result.financialYearLabel}`,
    `Assessment date: ${formatDate(result.assessedAt)}`,
    `Methodology ${result.methodologyVersion}  ·  Rules ${result.regulatoryConfigVersion}`,
  ]
  doc.text(coverLines, MARGIN, 100)
  doc.setFontSize(10)
  const disclaimer = doc.splitTextToSize(
    'This report records a self-assessment. It is not legal advice, a SEBI filing determination, a certification, or an assurance opinion. Cohort conclusions depend on answers the tool does not verify against exchange rankings. User-supplied answers are separate from the regulatory notes below.',
    CONTENT_W,
  ) as string[]
  doc.text(disclaimer, MARGIN, 140)
  doc.text(BRSR_TOOL_URL, MARGIN, 270)

  doc.addPage()
  let y = 22
  y = heading(doc, y, '1. Executive summary')
  const scoreText =
    result.overall.score == null
      ? 'A numeric score was not calculated because too little of the questionnaire was answered.'
      : result.overall.provisional
        ? `Provisional self-assessment score: ${result.overall.score} / 100. ${result.overall.bandLabel}.`
        : `Self-assessment score: ${result.overall.score} / 100. ${result.overall.bandLabel}.`
  y = paragraph(
    doc,
    y,
    `${scoreText} Completion ${result.overall.completion}%. Critical gaps: ${result.overall.criticalGaps}. Indicators to review: ${result.overall.reviewItems}. Unknown answers: ${result.overall.unknown}.`,
  )
  y = paragraph(
    doc,
    y,
    `Applicability status: ${statusLabel(result.applicability.overallStatus)}. ${result.applicability.conclusions.find((item) => item.id === 'filing')?.conclusion ?? ''}`,
  )
  if (result.keyFindings.length === 0) {
    y = paragraph(doc, y, 'No scored gaps were produced from the answers given.')
  }
  for (const finding of result.keyFindings) {
    y = bullet(doc, y, `${priorityLabel(finding.priority)} — ${finding.label}: ${finding.responseLabel}. ${finding.whyItMatters}`)
  }

  y = heading(doc, y, '2. Applicability and assumptions')
  y = paragraph(doc, y, 'Regulatory statements below come from the versioned configuration. The company facts are only what was entered.')
  for (const item of result.applicability.conclusions) {
    y = ensure(doc, y, 28)
    setText(doc, INK)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10)
    doc.text(item.title, MARGIN, y)
    y += 5
    y = paragraph(doc, y, item.conclusion)
    y = paragraph(doc, y, `Reason: ${item.reason}`)
    y = paragraph(
      doc,
      y,
      `Status: ${statusLabel(item.status)}. Year: ${item.financialYear}. Inputs: ${item.inputs.map((input) => `${input.label}: ${input.value}`).join('; ') || 'None'}.`,
    )
    y = paragraph(doc, y, `Next step: ${item.nextStep}`)
    const links = item.sourceIds
      .map((id) => sourceById(id))
      .filter((source) => source)
      .map((source) => source?.url)
      .join(' ')
    if (links) y = paragraph(doc, y, `Sources: ${links}`)
  }
  y = paragraph(doc, y, 'Assumptions')
  for (const assumption of result.applicability.assumptions) y = bullet(doc, y, assumption)
  if (result.naExplanation) {
    y = paragraph(doc, y, `Note supplied by the user on “not applicable” answers: ${result.naExplanation}`)
  }

  y = heading(doc, y, '3. Overall score and dimensions')
  y = paragraph(
    doc,
    y,
    'Scores use 0, 25, 50, 75 and 100 for the five readiness levels. Not applicable is excluded. Not sure scores nothing and is counted as unknown. Bands are EcopsHub labels, not SEBI categories.',
  )
  for (const dimension of result.dimensions) {
    const value = dimension.score == null ? dimension.pathway.replaceAll('_', ' ') : `${dimension.score} / 100`
    y = bullet(
      doc,
      y,
      `${dimension.label}: ${value}. Coverage ${dimension.coverage}%. Answered ${dimension.answered}, unknown ${dimension.unknown}, excluded ${dimension.excluded}, unanswered ${dimension.unanswered}.`,
    )
  }

  y = heading(doc, y, '4. Disclosure readiness by BRSR section')
  for (const section of result.sectionScores) {
    y = bullet(doc, y, `${section.label}: ${section.score == null ? section.pathway : `${section.score} / 100`} (coverage ${section.coverage}%).`)
  }

  y = heading(doc, y, '5. NGRBC principle-wise readiness')
  for (const principle of result.principleScores) {
    const statement = NGRBC_PRINCIPLES.find((item) => item.id === principle.id)?.statement
    y = bullet(
      doc,
      y,
      `${principle.label}: ${principle.score == null ? principle.pathway : `${principle.score} / 100`}. ${statement ?? ''}`,
    )
  }

  y = heading(doc, y, '6. Environmental and GHG data gaps')
  y = gapSection(doc, y, result, 'environmental')
  y = heading(doc, y, '7. Social and governance gaps')
  y = gapSection(doc, y, result, 'social_governance')
  y = heading(doc, y, '8. Evidence and internal controls')
  y = gapSection(doc, y, result, 'evidence')

  y = heading(doc, y, '9. BRSR Core readiness')
  const core = result.dimensions.find((dimension) => dimension.id === 'brsr_core')
  y = paragraph(
    doc,
    y,
    core?.pathway === 'not_in_pathway'
      ? 'BRSR Core questions were not in the pathway for these answers (for example an unlisted company, or a listed company outside the top 1,000).'
      : `Core preparedness: ${core?.score == null ? core?.pathway : `${core?.score} / 100`}. Catalogue status: ${BRSR_CORE_CATALOGUE_STATUS}. Attributes are summarised from the July 2023 annexure and should be checked against the current master-circular annexure.`,
  )
  for (const metric of BRSR_CORE_METRICS) {
    y = bullet(doc, y, `${metric.attribute} — ${metric.parameter} (${metric.brsrCrossReference}).`)
  }
  y = gapSection(doc, y, result, 'brsr_core')

  y = heading(doc, y, '10. Value-chain readiness')
  const chain = result.dimensions.find((dimension) => dimension.id === 'value_chain')
  y = paragraph(
    doc,
    y,
    `Classification: ${result.applicability.valueChainBasis}. Score pathway: ${chain?.pathway ?? 'n/a'}. ${chain?.score == null ? '' : `Score ${chain.score} / 100.`}`,
  )
  y = gapSection(doc, y, result, 'value_chain')

  y = heading(doc, y, '11. Prioritised corrective-action plan')
  y = paragraph(doc, y, 'Priority 1 is a potentially mandatory reporting gap. Priority 2 is an important data or evidence gap. Priority 3 is a process improvement. Optional items are leadership indicators and are capped in this list.')
  if (result.actions.length === 0) y = paragraph(doc, y, 'No actions were generated.')
  for (const action of result.actions) {
    y = bullet(doc, y, `${priorityLabel(action.priority)} · ${action.section} · ${action.title}. ${action.detail} (${action.relation.replaceAll('_', ' ')}).`)
  }

  y = heading(doc, y, '12. Strengths')
  if (result.strengths.length === 0) {
    y = paragraph(doc, y, 'No indicator was reported as documented and evidenced, or as a repeatable reviewed process. Nothing has been added to fill this section.')
  }
  for (const strength of result.strengths) {
    y = bullet(doc, y, `${strength.section}: ${strength.label} — ${strength.responseLabel}.`)
  }

  y = heading(doc, y, '13. Where a data system can help')
  y = paragraph(
    doc,
    y,
    'Companies that scored weakly on activity data, emission factors, metric ownership or evidence retention usually need a repeatable collection process rather than another spreadsheet at year end. A sustainability data and carbon-accounting system can hold facility activity data, factor libraries, review history and the same numbers for BRSR tables. That is a preparation aid. It does not itself complete a SEBI filing or an assessment engagement.',
  )

  y = heading(doc, y, '14. Scoring methodology and limitations')
  y = paragraph(
    doc,
    y,
    'Each scored question uses the maturity scale. Weights are higher for essential environmental, disclosure and evidence indicators. Leadership indicators, including Scope 3 and green credits, use a weight below 1 and cannot by themselves create a Priority 1 gap. Dimension scores are combined with fixed weights. A dimension with no visible questions is left out. The overall figure is provisional when required questions are unanswered, when disclosure, environmental or evidence coverage is under 70%, or when more than about a third of required answers are “not sure”.',
  )
  y = paragraph(
    doc,
    y,
    'Limitations: answers are self-reported; rankings are not verified; the value-chain partner threshold is flagged for review and is not applied; BRSR Core parameter text is a summary pending a check of the current master-circular annexure; no calendar filing deadline is stated because it was not re-verified; this is not legal advice.',
  )

  y = heading(doc, y, '15. Regulatory references')
  for (const source of REGULATORY_SOURCES) {
    y = bullet(
      doc,
      y,
      `${source.title}. ${source.authority}. ${source.documentDate}. ${source.circularNumber ?? 'No circular number'}. Status: ${source.status}. Last reviewed ${source.lastReviewed}. ${source.url}`,
    )
  }

  footer(doc)
  return doc
}

function gapSection(doc: Doc, y: number, result: BrsrResult, dimension: string): number {
  const questions = new Set(
    result.gaps.filter((gap) => gapMatches(gap.questionId, dimension, result)).map((gap) => gap.questionId),
  )
  const gaps = result.gaps.filter((gap) => questions.has(gap.questionId))
  if (gaps.length === 0) return paragraph(doc, y, 'No gap in this group was produced from the scored answers.')
  for (const gap of gaps) {
    y = bullet(
      doc,
      y,
      `${gap.section}: ${gap.label}. Response: ${gap.responseLabel}. ${gap.whyItMatters} Action: ${gap.action} (${priorityLabel(gap.priority)}; ${gap.relation.replaceAll('_', ' ')}).`,
    )
  }
  return y
}

function gapMatches(questionId: string, dimension: string, result: BrsrResult): boolean {
  const dimensionIds: Record<string, string[]> = {
    environmental: ['ghg_scope1', 'ghg_scope2', 'ghg_scope3', 'ghg_method', 'energy_data', 'water_data', 'waste_data', 'env_air', 'env_controls'],
    social_governance: ['soc_workforce', 'soc_diversity_safety', 'soc_training', 'soc_human_rights', 'soc_anticorruption', 'soc_consumers', 'soc_community', 'gov_board'],
    evidence: ['ev_traceability', 'ev_methodology', 'ev_consistency', 'ev_ownership', 'ev_corrections', 'ev_reproduction'],
    brsr_core: ['core_environment', 'core_social', 'core_governance', 'core_assessment'],
    value_chain: ['vc_customer_requirements', 'vc_supplier_collection', 'vc_coverage', 'vc_supplier_evidence', 'vc_procurement'],
  }
  return (dimensionIds[dimension] ?? []).includes(questionId) && result.gaps.some((gap) => gap.questionId === questionId)
}

function priorityLabel(priority: string): string {
  if (priority === 'p1') return 'Priority 1'
  if (priority === 'p2') return 'Priority 2'
  if (priority === 'p3') return 'Priority 3'
  return 'Optional'
}

function statusLabel(status: string): string {
  if (status === 'verified_rule') return 'Verified rule'
  if (status === 'conditional') return 'Conditional conclusion'
  return 'Requires verification'
}

function formatDate(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
}
